/**
 * Reusable 3-level drill-down tree for the Dispatch Section Reports.
 *
 * Renders: Category 4 → "Design – Shade – Size – Description" → Staff
 *
 * Shared by Pieces Processed, SFG Put Away, SFG Picking (and Turn C's
 * FG Put Away / FG Picking / FG Packing), so the visual + interaction
 * stays consistent and CSV export can later be added in one place.
 *
 * Props:
 *   data        — { summary: { total_pieces, categories, items, staff },
 *                   categories: [ { category_4, total_pieces, items:[
 *                     { item_barcode, design_name, shade, size, description,
 *                       total_pieces, staff:[ { staff_id, staff_name,
 *                       pieces, bundles } ] } ] } ] } | null
 *   loading     — boolean
 *   accent      — tailwind colour key matching the report's drill accent
 *                 ("teal", "indigo", "amber", "rose", "purple", "emerald")
 *   testIdScope — prefix used in data-testids for selector stability
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Layers, Package, RefreshCw } from 'lucide-react';

const ACCENTS = {
  teal: { tile: 'bg-teal-950/40 border-teal-500/30', total: 'text-teal-400' },
  indigo: { tile: 'bg-indigo-950/40 border-indigo-500/30', total: 'text-indigo-400' },
  amber: { tile: 'bg-amber-950/40 border-amber-500/30', total: 'text-amber-400' },
  rose: { tile: 'bg-rose-950/40 border-rose-500/30', total: 'text-rose-400' },
  purple: { tile: 'bg-purple-950/40 border-purple-500/30', total: 'text-purple-400' },
  emerald: { tile: 'bg-emerald-950/40 border-emerald-500/30', total: 'text-emerald-400' },
};

export default function ItemBreakdownTree({ data, loading, accent = 'teal', testIdScope = 'ib' }) {
  const [expandedCats, setExpandedCats] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const a = ACCENTS[accent] || ACCENTS.teal;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }
  if (!data || !data.categories?.length) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid={`${testIdScope}-empty`}>No data for this period</div>
    );
  }
  const s = data.summary || {};

  return (
    <div className="px-4 space-y-3">
      <div className="grid grid-cols-4 gap-2" data-testid={`${testIdScope}-summary`}>
        {[
          { label: 'Pieces', val: s.total_pieces },
          { label: 'Categories', val: s.categories },
          { label: 'Items', val: s.items },
          { label: 'Staff', val: s.staff },
        ].map((tile) => (
          <div key={tile.label} className={`${a.tile} rounded-xl p-2 border`}>
            <p className="text-lg font-bold text-white tabular-nums">{(tile.val || 0).toLocaleString()}</p>
            <p className="text-gray-400 text-[10px]">{tile.label}</p>
          </div>
        ))}
      </div>

      {data.categories.map((cat) => {
        const catKey = cat.category_4;
        const catOpen = !!expandedCats[catKey];
        return (
          <div key={catKey} className="bg-gray-800 rounded-xl overflow-hidden" data-testid={`${testIdScope}-cat-${catKey}`}>
            <button
              onClick={() => setExpandedCats((p) => ({ ...p, [catKey]: !p[catKey] }))}
              className="w-full flex items-center justify-between p-3 active:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                {catOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                <Layers className={`w-4 h-4 ${a.total} flex-shrink-0`} />
                <span className="font-semibold text-white text-sm truncate">{catKey}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">({cat.items.length})</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className={`text-lg font-bold ${a.total} tabular-nums`}>{cat.total_pieces.toLocaleString()}</span>
                <span className="text-xs text-gray-500">pcs</span>
              </div>
            </button>
            {catOpen && (
              <div className="border-t border-gray-700">
                {cat.items.map((it) => {
                  const itemKey = `${catKey}::${it.item_barcode}`;
                  const itemOpen = !!expandedItems[itemKey];
                  const title = [it.design_name, it.shade, it.size, it.description].filter(Boolean).join(' · ') || it.item_barcode;
                  return (
                    <div key={itemKey} className="border-t border-gray-700/50" data-testid={`${testIdScope}-item-${itemKey}`}>
                      <button
                        onClick={() => setExpandedItems((p) => ({ ...p, [itemKey]: !p[itemKey] }))}
                        className="w-full flex items-center justify-between py-2.5 px-3 pl-9 active:bg-gray-700/60 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {itemOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                          <Package className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-200 truncate">{title}</span>
                          <span className="text-[10px] text-gray-500 flex-shrink-0">({it.staff.length})</span>
                        </div>
                        <span className="text-sm font-bold text-white tabular-nums flex-shrink-0">{it.total_pieces.toLocaleString()}</span>
                      </button>
                      {itemOpen && (
                        <div className="bg-gray-900/40 divide-y divide-gray-800">
                          {it.staff.map((st) => (
                            <div key={`${st.staff_id}-${itemKey}`} className="flex items-center justify-between py-2 px-3 pl-16" data-testid={`${testIdScope}-staff-${st.staff_id}-${itemKey}`}>
                              <div className="min-w-0">
                                <p className="text-xs text-gray-100 truncate">{st.staff_name || st.staff_id}</p>
                                <p className="text-[10px] text-gray-500">{st.staff_id} • {st.bundles} bundles</p>
                              </div>
                              <span className="text-sm font-bold text-emerald-400 tabular-nums">{st.pieces.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
